/**
 * CycleDetector Service - Zustand Store
 * Single Responsibility: Detect circular form references
 */

import { create } from 'zustand';
import { useChildFormRegistryStore } from './ChildFormRegistryStore';
import type { ChildFormConfig } from '../types/index';

export interface CycleDetectorStore {
  /**
   * Check if a form has circular reference to itself
   */
  detect: (
    formID: string,
    childFormConfigs: ChildFormConfig[],
    visited?: Set<string>
  ) => boolean;

  /**
   * Get path to cycle (for error reporting)
   */
  getPath: (
    formID: string,
    childFormConfigs: ChildFormConfig[],
    visited?: string[]
  ) => string[];
}

export const useCycleDetectorStore = create<CycleDetectorStore>((set, get) => ({
  detect: (
    formID: string,
    childFormConfigs: ChildFormConfig[],
    visited = new Set<string>()
  ): boolean => {
    // If we've seen this form before, we have a cycle
    if (visited.has(formID)) {
      return true;
    }

    visited.add(formID);

    // Check all child forms
    for (const childConfig of childFormConfigs) {
      const childRegistry = useChildFormRegistryStore.getState();
      const childForm = childRegistry.get(childConfig.formID);

      // If child is a FormContainer, recursively check it
      if (childForm && 'config' in childForm && 'childForms' in (childForm.config as object)) {
        const childConfigs = (childForm.config as any).childForms || [];
        if (get().detect(childConfig.formID, childConfigs, new Set(visited))) {
          return true;
        }
      }
    }

    return false;
  },

  getPath: (
    formID: string,
    childFormConfigs: ChildFormConfig[],
    visited: string[] = []
  ): string[] => {
    // If we've seen this form before, we found the cycle
    if (visited.includes(formID)) {
      return [...visited, formID];
    }

    const newPath = [...visited, formID];

    // Check all child forms
    for (const childConfig of childFormConfigs) {
      const childRegistry = useChildFormRegistryStore.getState();
      const childForm = childRegistry.get(childConfig.formID);

      if (childForm && 'config' in childForm && 'childForms' in (childForm.config as object)) {
        const childConfigs = (childForm.config as any).childForms || [];
        const cyclePath = get().getPath(
          childConfig.formID,
          childConfigs,
          newPath
        );

        // If we found a cycle
        if (cyclePath.length > newPath.length) {
          return cyclePath;
        }
      }
    }

    return [];
  },
}));
